/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { GuideModel, PlaceModel } from '@app/core/models';
import { SnackbarService, StorageService } from '@app/core/services';
import { GuideApiService } from '@app/shared/services/api';

const TSTATE_KEY = makeStateKey<GuideModel>('guide-resolver');

@Injectable()
export class GuideResolver implements Resolve<GuideModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly btrGuideApiService: GuideApiService,
    private readonly transferState: TransferState,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly storageService: StorageService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<GuideModel> | Promise<GuideModel> | GuideModel | null {
    const guideSlug = route.paramMap.get('guideSlug');
    // console.log(guideSlug);
    return this.isServer
      ? this.serverResolve(guideSlug)
      : this.browserResolve(guideSlug);
  }

  private browserResolve(
    guideSlug: string,
  ): Observable<GuideModel> | GuideModel {
    const tmp = this.transferState.get<Pick<GuideModel, keyof GuideModel>>(
      TSTATE_KEY,
      null,
    );

    if (tmp != null) {
      this.transferState.remove<GuideModel>(TSTATE_KEY);
      return GuideModel.copyObj(tmp);
    }

    if (this.storageService.GUIDE?.slug === guideSlug) {
      return GuideModel.copyObj(this.storageService.GUIDE);
    }

    return this.btrGuideApiService.getGuideDetailsBySlug(guideSlug).pipe(
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.log(err);
        return of(null);
      }),
    );
  }

  private serverResolve(guideSlug: string): Observable<GuideModel> {
    return this.btrGuideApiService.getGuideDetailsBySlug(guideSlug).pipe(
      tap(data => {
        this.transferState.set<GuideModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.log(err);
        return of(null);
      }),
    );
  }
}
